import React, { useCallback, useMemo, useState } from "react";
import { PaymentMethod } from "../../../../common/enums";
import { WorkflowMode } from "../../common/enums";
import { AchPaymentPrefInput } from "../../Components/AchPaymentPrefInput";
import { CheckPaymentPrefInput } from "../../Components/CheckPaymentPrefInput";
import { SelectPaymentMethod } from "../../Components/SelectPaymentMethod";
import { type ChildComponentProps } from "../../Components/types";
import { WirePaymentPrefInput } from "../../Components/WirePaymentPrefInput";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { type ConsolidatedPaymentPrefContextType } from "../../Context/types";
import { type BasePaymentPrefWorkflowProps } from "./types";

const getPaymentPrefForm = (paymentMethod?: PaymentMethod) => {
  switch (paymentMethod) {
    case PaymentMethod.ACH:
      return AchPaymentPrefInput;
    case PaymentMethod.Check:
      return CheckPaymentPrefInput;
    case PaymentMethod.Wire:
      return WirePaymentPrefInput;
    default:
      throw new Error("unsupported payment method");
  }
};

const getWorkflowComponents = ({
  selectedPaymentMethod,
  isExistingPaymentPref,
  workflowMode,
  prefixComponents,
  suffixComponents,
}: Pick<ConsolidatedPaymentPrefContextType, "selectedPaymentMethod" | "isExistingPaymentPref"> &
  Pick<
    BasePaymentPrefWorkflowProps,
    "prefixComponents" | "suffixComponents" | "workflowMode"
  >) => {
  const workflowComponents: React.FC<ChildComponentProps>[] = [];

  if (prefixComponents) {
    workflowComponents.push(...prefixComponents);
  }

  if (!isExistingPaymentPref) {
    if (workflowMode === WorkflowMode.Create) {
      workflowComponents.push(SelectPaymentMethod);
    }

    if (selectedPaymentMethod) {
      const paymentFormInput = getPaymentPrefForm(selectedPaymentMethod);
      workflowComponents.push(paymentFormInput);
    }
  }

  if (suffixComponents) {
    workflowComponents.push(...suffixComponents);
  }

  return workflowComponents;
};

export const BasePaymentPrefWorkflow: React.FC<BasePaymentPrefWorkflowProps> = ({
  onExit,
  onSuccess,
  workflowMode,
  prefixComponents,
  suffixComponents,
}) => {
  const { selectedPaymentMethod, isExistingPaymentPref } = useConsolidatedPaymentPrefContext();
  const [step, setStep] = useState(0);

  const workflowComponents = useMemo(
    () =>
      getWorkflowComponents({
        selectedPaymentMethod,
        isExistingPaymentPref,
        workflowMode,
        prefixComponents,
        suffixComponents,
      }),
    [
      isExistingPaymentPref,
      prefixComponents,
      selectedPaymentMethod,
      suffixComponents,
      workflowMode,
    ],
  );

  const handleContinuePress = useCallback(() => {
    if (step < workflowComponents.length - 1) {
      setStep(step + 1);
    } else {
      onSuccess();
    }
  }, [onSuccess, step, workflowComponents.length]);

  const handleBackPress = useCallback(() => {
    if (step > 0) {
      setStep(step - 1);
    } else {
      onExit();
    }
  }, [onExit, step]);

  const CurrentStep = workflowComponents[step];

  return (
    <CurrentStep
      onContinueClick={handleContinuePress}
      onBackClick={handleBackPress}
      onBackButtonName={step === 0 ? "Cancel" : undefined}
    />
  );
};
